<template>
  <div class="full-page-width view-shipment">
    <div class="add-button c-float m-b-25">
      <div class="flex-center float-left">
        <Btn
          @click.native="routeIt"
          class="light bordered empty d-inline-block m-r-15 fix-40"
          :icon="{
            name: 'arrowLeftBig',
            size: '20',
          }" />
        <p class="user-name" v-if="get_single_pickup !== null">{{ `${$t('pickup_at')} ${get_single_pickup.full_address}` }}</p>
      </div>
      <Dropdown
        class="float-right drop-right"
        :buttonText="$t('Actions')"
        :dropdownOptions="dropdownOptions"
        @optionSelected="optionSelected" />
    </div>
    <!-- TOP BAR -->
    <div v-if="get_single_pickup !== null">
      <div class="shipment-info-box card no-pad m-b-40">
        <div class="top-row flex">
          <div class="info-box fix-width-position">
            <p class="small-text">{{ formatDate(get_single_pickup.pickup_date, 'shipment') }} {{get_single_pickup.time_from }} - {{get_single_pickup.time_until}} {{get_single_pickup.time_zone ? '(' + get_single_pickup.time_zone + ')' : ''}}</p>
            <p class="big-text">
              <span class="cut-text medium">{{ get_single_pickup.city }}</span>
              <span class="align-middle">{{ `, ${ countryAlpha2(get_single_pickup.country_id) }` }}</span>
            </p>
            <p class="small-text tooltip-holder">
              <span class="cut-text small">{{ get_single_pickup.company_name ||  get_single_pickup.name}}</span>
              <span class="d-inline-block info-tooltip">
                <Icon
                  v-tooltip="{ ref: 'tooltipRef'}"
                  name="info"
                  size="15" />
            <div class="info-tooltip-window" ref="tooltipRef">
              <p v-if="get_single_pickup.company_name">{{ get_single_pickup.company_name }}</p>
              <p v-if="get_single_pickup.name">{{ get_single_pickup.name }}</p>
              <p v-if="get_single_pickup.address_1">{{ get_single_pickup.address_1 }}</p>
              <p v-if="get_single_pickup.address_2">{{ get_single_pickup.address_2 }}</p>
              <p v-if="get_single_pickup.post_code || get_single_pickup.city">{{ `${get_single_pickup.post_code} ${get_single_pickup.city}` }}</p>
              <p v-if="get_single_pickup.state">{{ get_single_pickup.state }}</p>
              <p v-if="get_single_pickup.country_id">{{ countryName(get_single_pickup.country_id) }}</p>
              <p v-if="get_single_pickup.phone">{{ `+${get_single_pickup.phone}` }}</p>
              <p v-if="get_single_pickup.email">{{ get_single_pickup.email }}</p>
            </div>
            </span>
            </p>
          </div>
          <div class="middle-box status-box" v-if="get_single_pickup != null">
            <div
              class="shipment-status-bar"
              :class="statusInfo(get_single_pickup.status).class">
              <svg class="stroke-dashed" xmlns="http://www.w3.org/2000/svg">
                <line x1="0" y1="0" x2="100%" y2="0" />
              </svg>
              <div class="icon-point">
                <p class="status-text">{{ statusInfo(get_single_pickup.status).text }}</p>
                <Icon
                  :name="statusInfo(get_single_pickup.status).icon"
                  size="15" />
              </div>
              <div class="point-one"></div>
              <div class="point-two"></div>
            </div>
            <p class="update-info">
              {{ `${$t('last_update')}` }} <span style="text-transform: lowercase">{{ `${formatDate(get_single_pickup.shipment_status_date, 'shipment_last_update')}` }} </span> |
              {{ get_single_pickup.shipment_status_message ? getStatusMessageText(get_single_pickup.shipment_status_message) : '' }}
            </p>
          </div>
          <div class="info-box fix-width-position">
            <p class="small-text">{{ `${get_single_pickup.packages_count} colli` }}</p>
            <p class="big-text">
              <span class="medium">{{parseFloat(get_single_pickup.weight ? get_single_pickup.weight : 0) | weight}} </span>
            </p>
            <p class="small-text">
              {{ `${getVolumeText(get_single_pickup.vol_dm3)}`}}<span style="vertical-align: super;
                        font-size: 10px;
                        line-height: 0px;">3</span>
            </p>
          </div>
        </div>
        <div class="separator medium"></div>
        <div class="bottom-row flex">
          <div class="info-box c-float w-33">
            <LogosSVG :name="get_single_pickup.shipping_agent_name" size="45" class="float-left m-r-15" />
            <div class="float-left">
              <div>
                <p class="big-info-text">
                  <span class="m-r-5">{{ get_single_pickup.trace_no || '-' }}</span>
                </p>
                <p class="small-info-text">{{ get_single_pickup.shipping_agent_name }}</p>
              </div>
            </div>
          </div>
          <div class="middle-box c-float w-33">
            <Icon
              :circle=true
              name="pen"
              size="17"
              class="float-left m-r-15" />
            <div class="float-left">
              <p class="big-info-text">{{ `${$t('reference')}: ${get_single_pickup.reference || '-'}` }}</p>
              <p class="small-info-text">{{ `${$t('NOTE_TO_DRIVER')}: ${get_single_pickup.driver_note || '-'}` }}</p>
            </div>
          </div>
          <div class="info-box c-float fix-width-position w-33 max-w-225">
            <Icon
              :circle=true
              :key="`iconKey-${get_single_pickup.id}`"
              :name="'savedItems'"
              size="20"
              class="float-left m-r-15" />
            <div class="float-left">
              <p class="big-info-text">{{ get_single_pickup.shipment_agents }}</p>
              <p class="small-info-text">{{ getGoodsTypesNames(get_single_pickup.shipment_goods_types_array) }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- LEFT PART - PRODUCTS -->
    <div class="row text-box">
      <div class="col-xs-8" v-if="get_single_pickup != null">
        <!-- DOCUMENTS -->
        <div class="m-b-40">
          <h2 class="m-b-20 section-title">{{ $t('documents') }}</h2>
          <div class="package-preview card-boxed">
            <div class="flex-boxed center">
              <!-- 1. Download EOD images -->
              <div class="info-box inf-abs c-float">
                <Icon
                  :circle=true
                  name="agreements"
                  size="20"
                  class="icon-left m-r-15" />
                <div class="float-left">
                  <p class="big-info-text">{{ $t('eod_manifest') }}</p>
                  <p class="small-info-text link pointer" @click="downloadPDF">
                    {{ $t('Download EOD') }}
                  </p>
                  <a
                    style="display: none"
                    :download="`EOD_${$route.params.id}`"
                    ref="downloadEODPDF"
                    :href="PickupEODPdfPath" />
                </div>
              </div>
              <!-- 2. POD images -->
              <div class="info-box inf-abs c-float" v-if="get_single_pickup.agent_pod_paths != null && get_single_pickup.agent_pod_paths != ''">
                <Icon
                  :circle=true
                  name="images"
                  size="20"
                  class="icon-left m-r-15" />
                <div class="float-left">
                  <p class="big-info-text">{{ $t('POD images') }}</p>
                  <p class="small-info-text link pointer" @click="downloadAgentImage">
                    {{ $t('Download images') }}
                  </p>
                  <a
                    style="display: none"
                    :download="`POD_images_${get_single_pickup.id}`"
                    ref="downloadAgentImage"
                    :href="agentImagePath" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- SHIPMENTS -->
        <div>
          <h2 class="m-b-20 section-title" v-if="get_pickup_shipments_list !== null">
            {{ $t('shipments')}}
            <span class="grayed">{{ `(${get_pickup_shipments_count})` }}</span>
          </h2>
          <DataTable
            :dropdown=false
            :tableHeader=false
            :tableHead=false
            tableAPI="BOOKED_PICKUP_SHIPMENTS"
            tableActions="BOOKED_PICKUP_SHIPMENTS"
            :apiID="$route.params.id"
            :actionsOptions="actionOptions"
            :totalCount="10"
            :checkbox=true
            :type=false
            :count="get_pickup_shipments_count"
            :list="get_pickup_shipments_list"
            :referenceData="referenceData"
            :rowActionParameter="rowActionParameter"
            :showRowValueWithPrefix=true
            @actionOptionTriggered="actionsOptionTriggered"
            @rowAction="rowAction" />
        </div>
      </div>
      <div class="col-xs-4" v-if="get_single_pickup != null">
        <!-- TIMELINE -->
        <h2 class="m-b-20 section-title">{{ $t('timeline') }}</h2>
        <div class="card details-box">
          <div v-if="get_single_pickup !== null" class="timeline-box">
            <svg class="stroke-dashed" xmlns="http://www.w3.org/2000/svg">
              <line x1="0px" y1="40px" x2="0px" y2="100%" />
            </svg>
            <div class="timeline-holder">
              <div
                v-for="(group, index) in timelineArray" :key="`group-${index}`"
                class="timeline-group">
                <div class="list-separator"><div></div></div>
                <h2 class="timeline-group-title">{{ index }}</h2>
                <div class="timeline-info">
                  <div v-for="(item, index) in group" :key="`groupItem-${index}`" class="info-box c-float">
                    <div v-if="[24, 25, 26, 27, 28, 29, 30, 31, 33, 34, 35, 43].includes(item.status_id)">
                      <Icon
                        :circle=true
                        :name="statusInfo(item.status_id).icon"
                        size="20"
                        class="float-left m-r-15" />
                      <div class="float-left" style="width: 100%;">
                        <p class="big-info-text">{{ `${formatTime(item.created_time)} | ${getStatusMessageText(item.message) || $t(statusInfo(item.status_id).text)}` }}</p>
                        <p v-if="item.addition != null || item.addition != undefined"
                           class="small-info-text" style="word-break: break-word;">{{ item.addition.split(",").join(", ") }}</p>
                      </div>
                    </div>
                    <div v-else>
                      <Icon
                        :circle=true
                        :name="statusInfo(item.status_id).icon"
                        :size="item.status_id == 3 ? '24' : '20'"
                        class="float-left m-r-15" />
                      <div class="float-left" style="width: 100%;">
                        <p class="big-info-text">{{ `${formatTime(item.created_time)} | ${$t(item.message || statusInfo(item.status_id).text)}` }}</p>
                        <p class="small-info-text" style="word-break: break-word;">{{ item.addition }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <Popup
      v-if="editReferencePopupOpened"
      :popupSmall=true
      :showConfirmButton=true
      :showSendButton=false
      @cancel="closePopup"
      @confirm="editBookedPickup">
      <template v-slot:header>
        <h2>{{ $t('edit')}}</h2>
      </template>
      <div class="col-xs-12 m-b-20">
        <Calendar
          :label="$t('pickup_date')"
          v-model="pickupDate"
          :date="getPickupDate"
          :rules="{required: true}" />
      </div>
      <div class="m-b-40">
        <message
          v-if="pickupTimeData.pickupTimeZone !== getBrowserTimeZone"
          style="margin-bottom: 25px" name="warning"
          :customIcon="'clock'"
          :data="data={data: getPickupTimeZoneMessage, type: 'custom-message'}">
        </message>
        <div class="col-xs-12 m-b-20">
          <InputSelect
            @change="returnHoursArrayFunction"
            ref="pickupTimeZone"
            :rules="{required: true}"
            :label="$t('timezone')"
            v-model="pickupTimeData.pickupTimeZone"
            :options="timeZones"/>
        </div>
        <div class="col-xs-12 m-b-20">
          <InputSelect
            ref="updateForm.pickupTimeFrom"
            :label="$t('ready_pickup_time')"
            v-model="pickupTimeData.pickupTimeFrom"
            :options="getTimeOptions.from" />
        </div>
        <div class="col-xs-12">
          <InputSelect
            ref="updateForm.pickupTimeUntil"
            :label="$t('latest_pickup_time')"
            v-model="pickupTimeData.pickupTimeUntil"
            :options="getTimeOptions.until" />
        </div>
      </div>
      <div class="m-b-40">
        <InputText
          class="col-xs-12 m-b-20 m-t-20"
          :label="$t('reference')"
          :rules="{required: false}"
          v-model="updateForm.reference"/>
      </div>
      <div class="m-b-40">
        <InputText
          class="col-xs-12 m-b-20"
          :label="$t('driver_note')"
          :rules="{required: false}"
          v-model="updateForm.driverNote"/>
      </div>
    </Popup>
    <Popup
      v-if="showMoveShipmentToBookedPickupPopup"
      min-height="400px"
      :popupLarge=true
      :showConfirmButton="!!Object.keys(selectedBookedPickup).length"
      @cancel="toggleMoveShipmentToBookedPickupPopup"
      @confirm="submitMoveShipmentToBookedPickup">
      <template v-slot:header>
        <h2>{{ $t('move_to_booked') }}</h2>
      </template>
      <div class="row">
        <div class="col-xs-12 m-b-20">
          <div class="row m-b-input">
            <div class="col-xs-12">
              <Calendar
                :label="$t('pickup_date')"
                v-model="moveShipmentToBookedPickupData.pickupDate"
                :date="getPickupDate"
                :rules="{required: true}" />
            </div>
          </div>
        </div>
      </div>
      <p>{{ $t('existing_pickup')}}</p>
      <div class="m-t-20">
        <div>
          <div>
            <div
              v-for="(existingPickup, index) in availableBookedPickups" :key="`mtb-row-${index}`" >
              <div class="toggle-card-box card no-padding">
                <div
                  @click="selectBookedPickupToMoveShipmentsIn(existingPickup)"
                  class="toggle-card-header"
                  :class="{ 'pickup-selected': !!Object.keys(selectedBookedPickup).length && parseInt(existingPickup.id) === parseInt(selectedBookedPickup.id) }">
                  <div class="info-box">
                    <div class="title-holder">
                      <LogosSVG :name="existingPickup.shipping_agent_name" size="62" class="m-r-20" />
                      <div>
                        <span class="sub-title m-b-5">{{ existingPickup.shipping_product_name}}</span>
                        <span class="title b-block m-b-5">{{ `${existingPickup.full_address}` }}</span>
                        <span class="sub-title">{{ `${existingPickup.packages_count} colli ` }} ({{ parseFloat(existingPickup.weight ? existingPickup.weight : 0) | weight }})</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Popup>
    <Popup
      v-if="showPickupPopup"
      min-height="400px"
      :popupLarge=true
      :showConfirmButton="false"
      @cancel="togglePickupPopup">
      <template v-slot:header>
        <h2>{{ $t('move_shipments_to_new_pickup') }}</h2>
      </template>
      <div>
        <div class="row">
          <div class="col-xs-12 m-b-20">
            <div class="row m-b-input">
              <div class="col-xs-5">
                <Calendar
                  :label="$t('pickup_date')"
                  v-model="pickupData.pickupDate"
                  :rules="{required: true}" />
              </div>
              <div class="col-xs-7">
                <InputText
                  v-cloak
                  v-model="pickupData.reference"
                  :label="$t('reference')"
                  :rules="{required: false}"
                />
              </div>
            </div>
            <InputSelect
              :class="isForeignPickupLocation ? 'm-b-input' : ''"
              :label="$t('pickup_location')"
              v-model="pickupLocationId"
              @change="checkForForeign"
              :countryFlag="true"
              :options="pickupLocationOptions"
            />
            <div class="row" v-if="isForeignPickupLocation">
              <div class="col-xs-6 m-b-20">
                <InputText
                  :label="$t('company_name')"
                  v-model="pickupData.company"
                  :rules="{required: true}" />
              </div>
              <div class="col-xs-6 m-b-20">
                <InputText
                  :label="$t('name')"
                  v-model="pickupData.attention"
                  :rules="{required: false}" />
              </div>
            </div>
            <AddressPicker
              class="m-b-input"
              v-if="isForeignPickupLocation"
              :label="$t('address_1')"
              placeholder="Search address"
              @addressSelected="changeAddress('pickup', $event)"
              v-model="pickupAddress"
            />
          </div>
        </div>
        <div class="row" v-if="isForeignPickupLocation">
          <div class="col-xs-6">
            <PhoneSelect v-model="pickupData.phone" :rules="{required: false}" />
          </div>
          <div class="col-xs-6">
            <InputText
              label="Email"
              v-model="pickupData.email"
              :rules="{required: false, email: false}" />
          </div>
        </div>
      </div>
      <!-- Products -->
      <message v-if="!productCardDataValid" name="warning" :data="data={data: this.productCardErrorMessage, type:'validation'}"></message>
      <div v-if="!reloadingProducts && productCardDataValid">
        <h2 class="m-b-20 p-t-30 section-title">{{ $t('pickup_options_on_this_address') }}</h2>
        <ProductCard
          v-for="(product, index) in productsList"
          :key="`product-${product.shipping_product_id}-${index}`"
          :product="product"
          :pickupLocationId="pickupLocationId"
          :pickupDate="pickupData.pickupDate"
          :pageType="'index'"
          :isOpened="(selectedProduct != null) && (selectedProduct.shipping_product_id == product.shipping_product_id)"
          :disableButton="disableButton"
          :loadingButton="loadingButton"
          @bookPickup="bookPickup"
          @selected="selectProduct" />
      </div>
    </Popup>
  </div>
</template>

<script>
/* eslint-disable */
import { mapGetters } from 'vuex';
import Calendar from '@/components/elements/inputs/calendar.vue';
import ProductCard from '@/components/pickup/productCard';
import mixins from '@/mixins/mixins.js';
import PickupPopupMixin from '@/mixins/bookPickupPopup';
import PickupTimeMixin from '@/mixins/pickupTime';
import DataTable from '@/components/table/index.vue';
import date from '@/mixins/date';
import Popup from '@/components/global/popup';
import Dropdown from '@/components/global/dropdown';
import Btn from '@/components/elements/buttons';
import LogosSVG from "@/assets/images/logosSVG";
import Checkbox from '@/components/elements/inputs/checkbox.vue';
import InputText from '@/components/elements/inputs/text.vue';
import InputSelect from '@/components/elements/inputs/select';
import AddressPicker from '@/components/global/addressPicker/index.vue';
import PhoneSelect from '@/components/elements/inputs/phone.vue';
import i18n from "@/i18n";

export default {
  name: 'BookedPickupView',
  mixins: [mixins, date, PickupPopupMixin, PickupTimeMixin],
  components: {
    Btn, LogosSVG, Dropdown, Popup, Checkbox, InputText, InputSelect,
    DataTable, Calendar, ProductCard, AddressPicker, PhoneSelect
  },
  data() {
    return {
      agentImagePath: '',
      disableButton: false,
      loadingButton: false,
      pickupLocationId: null,
      isForeignPickupLocation: false,
      productCardDataValid: false,
      showManualShipmentBlock: false,
      productCardErrorMessage: '',
      productsList: null,
      selectedProduct: null,
      reloadingProducts: false,
      strokeOptions: {
        strokeColor: '#017a75',
        // strokeColor: '#0088FF',
        strokeWeight: 6,
        strokeOpacity: 0.6
      },
      product: {},
      loaded:false,
      updateForm: {
        reference: '',
        driverNote: '',
      },
      pickupDate: new Date(),
      showMoveShipmentToBookedPickupPopup: false,
      showPickupPopup: false,
      availableBookedPickups: [],
      selectedBookedPickup: {},
      moveShipmentToBookedPickupData: {
        pickupDate: ''
      },
      checkedShipmentIds: [],
      invoicePdfPath: '',
      waybillPdfPath: '',
      status: null,
      additionalFilePdfPath: [],
      waybillsPdfPath: '',
      editReferencePopupOpened: false,
      additionalFileType: '',
      additionalFileName: '',
      additionalFile: null,
      emailPopupOpened: false,
      selectedShipper: true,
      selectedReciever: false,
      selectedOther: false,
      otherMail: '',
      emailMessage: '',
      waybillEmails: [],
      updateIterator: 1,
      totalCount: 10,
      referenceData: [
        {
          shipping_product_name: '',
          shipping_agent_name: '',
        },
        {
          packages_count_and_goods_type: '',
          trace_no: '',
        },
        {
          sender_reference: `${this.$t('Sender ref.:')}`,
          receiver_reference: `${this.$t('Reciever ref.:')}`,
        },
      ],
      rowActionParameter: {
        param: 'id',
        text: `${this.$t('View shipment')}`,
      },
      type: {
        type: false,
        data: 'status',
      },
      PickupEODPdfPath: '',
      pdfFilePath: '',
    };
  },
  mounted() {
    this.getComponentData()
  },
  computed: {
    ...mapGetters([
      'get_single_pickup',
      'get_parcel_types',
      'get_parcel_type_name',
      'get_subaccounts',
      'get_additional_file_types',
      'get_pickup_shipments_list',
      'get_pickup_shipments_count',
      'get_pickup_shipment_by_id',
      'get_active_subaccount',
      'get_pickup_locations'
    ]),
    getPickupDate() {
      const todayDate = new Date();
      const pickupDate = new Date(this.get_single_pickup.pickup_date)
      const date =  pickupDate < todayDate ? todayDate : pickupDate;

      return this.getFormatedDate(date).date;
    },
    filteredActionsOptions() {
      return this.showActions ? this.actionsOptions : [];
    },
    timelineArray() {
      let timelineArray = {};

      this.get_single_pickup.statuses.forEach((status) => {
        const createdAtFormatted = this.formatDate(status.created_at, 'ccc d MMM yyyy');

        if(timelineArray[createdAtFormatted] == null){
          timelineArray[createdAtFormatted] = [];
        }
        timelineArray[createdAtFormatted].push(status);
      });

      return timelineArray;
    },
    goodsTypeText(shipment) {
      if (!shipment.additional_info_array.goods_type_info) {
        return false;
      }

      const goodsTypeInfo = shipment.additional_info_array.goods_type_info;

      return Object.entries(goodsTypeInfo)
        .map(([type, count]) => `${count} x ${this.$t(type)}`)
        .join(', ');
    },
    filterOptions() {
      return {
        filtersMenu: this.get_filters_menu,
        activeFilter: this.get_active_filter,
        tableName: 'bookedPickups',
        tableApi: 'pickup/get-booked-list',
        filterFields: {
          status: {
            label: 'Status',
            type: 'select',
            value: {
              value: '',
              additional: '',
            },
            options: [
              {text: 'Canceled', value: '5'},
              {text: 'Printed', value: '3'},
              {text: 'On hold', value: '23'},
              {text: 'Sent', value: '4'},
              {text: 'Recived by agent', value: '12'},
              {text: 'On the way', value: '13'},
              {text: 'Issue', value: '16'},
              {text: 'In pickup point', value: '15'},
              {text: 'Delivered', value: '14'},
            ],
          },
          ship_date: {
            label: 'Ship date',
            type: 'date',
            value: {
              value: null,
              additional: '',
            },
          },
          created_at: {
            label: 'Created date',
            type: 'date',
            value: {
              value: null,
              additional: '',
            },
          },
          subaccount_id: {
            label: 'Account',
            type: 'input',
            value: {
              value: '',
              additional: '',
            },
          },
          shipping_agent_name: {
            label: 'Carrier',
            type: 'input',
            value: {
              value: '',
              additional: '',
            },
          },
          shipping_product_name: {
            label: 'Product name',
            type: 'input',
            value: {
              value: '',
              additional: '',
            },
          },
          weight: {
            label: 'Weight',
            type: 'options',
            value: {
              value: '',
              additional: '',
            },
          },
        }
      }
    },
    dropdownOptions() {
      const options = [
        {
          text: `${this.$t('Download EOD')}`,
          subText: `${this.$t('Download EOD pdf')}`,
          icon: 'endOfDay',
        }
      ];

      //If not status On the way, delivered, canceled then show edit option
      if (![13, 14, 5].includes(parseInt(this.status))) {
        options.push({
          text: `${this.$t('edit')}`,
          subText: `${this.$t('edit_subtext')}`,
          icon: 'pen',
        });
      }

      // Show cancel option only if status is PRINTED (3), ISSUE(16), WAITING FOR CARRIER APPROVAL (34), APPROVED (35)
      if ([3, 16, 34, 35].includes(parseInt(this.status))) {
        options.push({
          text: `${this.$t('cancel')}`,
          subText: `${this.$t('cancel_pickup_subtext')}`,
          icon: 'ban',
        });
      }

      return options;
    },
    actionOptions() {
      const options = [];

      // PRINTED (3), WAITING_FOR_CARRIER_APPROVAL(34), APPROVED(35), CANCELED(5), ISSUE (16)
      //if ([3, 34, 35, 5, 16].includes(parseInt(this.status))) {
      options.push({
          text: this.$t('remove'),
          subText: this.$t('remove_from_pickup_subtext'),
          icon: 'deleteForever',
        },
        {
          text: this.$t('move_to_existing'),
          subText: this.$t('move_to_existing_subtext'),
          icon: 'swapVertical',
        },
        {
          text: this.$t('move_shipments_to_new_pickup'),
          subText: this.$t('move_shipments_to_new_pickup_subtext'),
          icon: 'deliveryTime',
        }
      );

      return options;
    },
    getTimeOptions() {
      this.returnHoursArrayFunction(this.pickupTimeData.pickupTimeZone)
      return this.returnHoursArray;
    },
    getPickupTimeZoneMessage()
    {
      return i18n.tc('pickup_time_and_zone_message', 2, {
        time: this.pickupTimeData.pickupTimeZoneCurrentTime,
        zone: this.pickupTimeData.pickupTimeZone
      })
    },
  },
  methods: {
    getStatusMessageText(message, breakpoint, part) {

      //Translate first index of array
      if (message && message.includes('@separator0@')) {
        const statusMsgArray = message.split('@separator0@');
        return `${this.$t(statusMsgArray[0])} ${statusMsgArray[1]}`;
      }

      if (breakpoint && message.includes(breakpoint)) {
        const statusMsgArray = message.split(breakpoint);
        const translationString = statusMsgArray[part] ? statusMsgArray[part].trim() : '';
        return `${this.$t(translationString)}`;
      }

      return `${this.$t(message)}`;
    },
    downloadAgentImage() {
      this.$store.dispatch('DOWNLOAD_PICKUP_POD', this.get_single_pickup.id).then((res) => {
        this.$nextTick(() => {
          let file = window.URL.createObjectURL(res);
          this.$refs.downloadAgentImage.href = file;
          this.agentImagePath = file;
          this.$refs.downloadAgentImage.click();
        })
      })
    },
    bookPickup(childData) {
      const payload = {
        old_booked_pickup_id: this.$route.params.id,
        shipment_ids: this.checkedShipmentIds,
        allowed_awbs: childData.product.allowed_shipment_awbs,
        subaccount_id: this.get_active_subaccount.id,
        pickup_date: this.pickupData.pickupDate,
        reference: this.pickupData.reference,
        pickup_time_zone: childData.pickup_time_zone,
        pickup_time_from: childData.pickup_time_from,
        pickup_time_until: childData.pickup_time_until,
        pickup_driver_note: childData.pickup_driver_note,
        shipping_product_id: childData.product.shipping_product_id,
        address: this.getRequestFormattedAddress(),
        shipments: []
      }
      this.disableButton = true;
      this.loadingButton = true;
      this.$store.dispatch('BOOK_PICKUP', payload).then((resp) => {
        this.togglePickupPopup();
        this.$router.push(`/pickups/booked/${resp.id}`);
      }).finally(() => {
        this.disableButton = false;
        this.loadingButton = false;
      });
    },
    togglePickupPopup() {
      this.showPickupPopup = !this.showPickupPopup;
      this.clearBookPickupData();
    },
    toggleUpdatePickupPopup() {
      this.$store.dispatch('GET_PICKUP_PRODUCT', this.$route.params.id).then((res) => {
        this.product = res.pickup_product;
        this.updateForm.reference = this.get_single_pickup.reference;
        this.updateForm.driverNote = this.get_single_pickup.driver_note;
        this.pickupTimeData.pickupTimeZoneCurrentTime = this.get_single_pickup.location_time;
        this.pickupTimeData.pickupTimeZone = this.get_single_pickup.time_zone
          ? this.get_single_pickup.time_zone
          : this.pickupTimeData.pickupTimeZone;
        this.pickupTimeData.pickupTimeFrom = this.get_single_pickup.time_from;
        this.pickupTimeData.pickupTimeUntil = this.get_single_pickup.time_until;
        this.returnHoursArrayFunction(this.pickupTimeData.pickupTimeZone);
        this.editReferencePopupOpened = true;
      });
    },
    actionsOptionTriggered(opt) {
      this.checkedShipmentIds = opt.list;
      if(opt.dropOption === 'dropOption1') {
        // Remove shipment from booked pickup, this can only happen when pickup is in booked (printed) status
        this.$store.dispatch('MOVE_SHIPMENTS_FROM_BOOKED_TO_NOT_PLANNED_PICKUP', {id: this.$route.params.id, shipmentIds: opt.list}).then((res) => {
          this.$store.dispatch('GET_BOOKED_PICKUP_SHIPMENTS', {id: this.$route.params.id});
        });
      }
      if(opt.dropOption === 'dropOption2') {
        this.toggleMoveShipmentToBookedPickupPopup();
        this.getAvailableBookedPickups(this.getPickupDate)
      }
      if(opt.dropOption === 'dropOption3') {
        this.pickupAddress = {
          address_1: this.get_single_pickup.address_1,
          address_2: this.get_single_pickup.address_2,
          post_code: this.get_single_pickup.post_code,
          city: this.get_single_pickup.city,
          country_id: this.get_single_pickup.country_id,
          state: this.get_single_pickup.state,
        };
        this.pickupData = {
          ...this.pickupData,
          company: this.get_single_pickup.company_name,
          attention: this.get_single_pickup.name,
          email: this.get_single_pickup.email,
          phone: {
            number: this.get_single_pickup.phone,
            country: this.getCountry(this.get_single_pickup.country_id, 'alpha2'),
          },
        };
        this.openCreatePickupPopup();
      }
    },
    rowAction(val) {
      const shipment = this.get_pickup_shipment_by_id(val);
      //If shipment is not vendor type, go to shipment view
      if (shipment.shipment_type !== 6) {
        this.$router.push(`/view-shipment/${val}`);
      }
    },
    getGoodsTypesNames(goodsTypes) {
      let result = [];
      goodsTypes.forEach((goodsType) => {
        if (this.goodTypeName(goodsType)) {
          result.push(this.$t(this.goodTypeName(goodsType).name));
        }
      })

      return result.join(', ');
    },
    countryAlpha2(id) {
      return this.getCountry(id, 'alpha2');
    },
    iconType(type) {
      if(type == 1) return 'truck';
      if(type == 5) return 'document';
      if(type == 6) return 'deliveryTime';
      if(type == 9) return 'fuel';
    },
    statusInfo(statusNumber) {
      if(parseInt(statusNumber) === 3){
        return {
          class: 'step1',
          icon: 'printer',
          text: 'Booked',
        };
      } else if(parseInt(statusNumber) === 23){
        return {
          class: 'step1',
          icon: 'ban',
          text: 'Waiting for departure',
        };
      } else if(parseInt(statusNumber) === 5) {
        return {
          class: 'step1',
          icon: 'deleteForever',
          text: 'Canceled',
        };
      } else if(parseInt(statusNumber) === 4 || parseInt(statusNumber) === 13 || parseInt(statusNumber) === 17 || parseInt(statusNumber) === 12) {
        return {
          class: 'step2',
          icon: 'delivery',
          text: 'Transit',
        };
      } else if (parseInt(statusNumber) === 15) {
        return {
          class: 'step3',
          icon: 'pin',
          text: this.$t('In Pickup Point'),
        };
      } else if(parseInt(statusNumber) === 8 || parseInt(statusNumber) === 16) {
        return {
          class: 'step3',
          icon: 'warning',
          text: 'Deviation',
        };
      } else if(parseInt(statusNumber) === 14) {
        return {
          class: 'step4',
          icon: 'check',
          text: this.$t('completed'),
        };
      } else if(parseInt(statusNumber) === 1) {
        return {
          class: 'step4',
          icon: 'check',
          text: 'Pending',
        };
      } else if(parseInt(statusNumber) === 24 || parseInt(statusNumber) === 25 || parseInt(statusNumber) === 26 || parseInt(statusNumber) === 27 || parseInt(statusNumber) === 28 || parseInt(statusNumber) === 29 || parseInt(statusNumber) === 30 || parseInt(statusNumber) === 31 || parseInt(statusNumber) === 33) {
        return {
          class: 'step2',
          icon: 'letter',
          text: 'Transit',
        };
      } else if(parseInt(statusNumber) === 32) {
        return {
          class: 'step1',
          icon: 'attach',
          text: 'File upload',
        };
      }
      else if(parseInt(statusNumber) === 34){
        return {
          class: 'step1',
          icon: 'clock',
          text: 'Waiting for approval',
        };
      }
      else if(parseInt(statusNumber) === 35){
        return {
          class: 'step1',
          icon: 'checkMan',
          text: 'Approved by carrier',
        };
      }
      else if(parseInt(statusNumber) === 43) {
        return {
          class: 'step2',
          icon: 'delivery',
          text: this.$t('picked_up')
        };
      }
      else {
        return {
          class: 'step1',
          icon: 'check',
          text: '',
        };
      }
    },
    optionSelected(val) {
      if (val === 'dropOption1') {
        this.downloadPDF();
      }
      if (val == 'dropOption2') {
        this.toggleUpdatePickupPopup();
      }
      if (val == 'dropOption3') {
        this.$store.dispatch('CANCEL_PICKUP', {id: this.get_single_pickup.id}).then(() => {
          this.getComponentData();
        });
      }
    },
    closePopup() {
      this.emailPopupOpened = false;
      this.editReferencePopupOpened = false
    },
    otherNotification() {
      this.selectedOther = !this.selectedOther;
      if(!this.selectedOther) {
        this.otherMail = '';
      }
    },
    emailWaybills() {
      let mailList = [];
      if(this.selectedShipper) {
        mailList.push(this.get_single_pickup.sender_email)
      }

      if(this.selectedReciever) {
        mailList.push(this.get_single_pickup.customer_address_email)
      }

      this.$store.dispatch('EMAIL_WAYBILLS', {
        emails: this.otherMail.length > 0 ? [...mailList, ...this.otherMail.split(',')] : [...mailList],
        message: this.emailMessage,
        id: this.get_single_pickup.id,
      }).then(() => {
        this.emailPopupOpened = false;
      });
    },
    editBookedPickup() {
      this.$store.dispatch('EDIT_BOOKED_PICKUP', {
        id: this.get_single_pickup.id,
        reference: this.updateForm.reference,
        driver_note: this.updateForm.driverNote,
        pickup_time_zone: this.pickupTimeData.pickupTimeZone,
        pickup_time_from: this.pickupTimeData.pickupTimeFrom,
        pickup_time_until: this.pickupTimeData.pickupTimeUntil,
        ship_date: this.pickupDate
      }).then(() => {
        this.closePopup()
        this.getComponentData()
      });
    },
    selectOptions(obj) {
      const options = [];
      for (const property in obj) {
        options.push({ text: obj[property], value: property });
      }
      return options;
    },
    addFile() {
      this.$refs.invButton.click();
    },
    newFile() {
      this.additionalFile = this.$refs.invButton.files[0]
      this.additionalFileName = this.$refs.invButton.files[0].name;
    },
    countryName(id){
      return this.getCountry(id, 'name');
    },
    goodTypeName(type) {
      if(parseInt(type) === 1) {
        return {
          name: 'Parcel',
          icon: 'savedItems',
        };
      }
      if(parseInt(type) === 2){
        return {
          name: 'Document',
          icon: 'documentGoods',
        };
      }
      if(parseInt(type) === 3){
        return {
          name: 'Pallet',
          icon: 'pallet',
        };
      }
      if(parseInt(type) === 4){
        return {
          name: 'Load',
          icon: 'delivery',
        };
      }
    },
    linkCall(val) {
      window.open(val, '_blank');
    },
    downloadPDF() {
      this.$store.dispatch('DOWNLOAD_PICKUP_EOD', this.get_single_pickup.id).then((res) => {
        this.$nextTick(() => {
          let file = window.URL.createObjectURL(res);
          this.$refs.downloadEODPDF.href = file;
          this.PickupEODPdfPath = file;
          this.$refs.downloadEODPDF.click();
        })
      })
    },
    downloadInvoice() {
      this.$store.dispatch('GET_CUSTOMS_PDF', this.get_single_pickup.id).then((res) => {
        this.$nextTick(() => {
          let file = window.URL.createObjectURL(res);
          this.$refs.downloadInvoicePDF.href = file;
          this.invoicePdfPath = file;
          this.$refs.downloadInvoicePDF.click();
        })
      })
    },
    downloadWaybill() {
      this.$store.dispatch('GET_LABEL', this.get_single_pickup.id).then((res) => {
        this.$nextTick(() => {
          let file = window.URL.createObjectURL(res);
          this.$refs.downloadWaybillPDF.href = file;
          this.waybillPdfPath = file;
          this.$refs.downloadWaybillPDF.click();
        })
      })
    },
    downloadAdditionalFile(type) {
      this.$store.dispatch('GET_ADDITIONAL_FILE', {shipment_id: this.get_single_pickup.id, type: type}).then((res) => {
        this.$nextTick(() => {
          let file = window.URL.createObjectURL(res);
          this.$refs[`downloadAdditionalFile${type}`][0].href = file;
          this.additionalFilePdfPath[type] = file;
          this.$refs[`downloadAdditionalFile${type}`][0].click();
        })
      })
    },
    routeIt() {
      this.$router.go(-1);
    },
    async getComponentData() {
      this.isForeignPickupLocation = true;
      this.$store.dispatch('CLEAR_SINGLE_PICKUP');
      this.$store.dispatch('GET_BOOKED_SINGLE_PICKUP', this.$route.params.id);
    },
    //MOVE SHIPMENT INTO BOOKED PICKUP
    toggleMoveShipmentToBookedPickupPopup() {
      this.showMoveShipmentToBookedPickupPopup = !this.showMoveShipmentToBookedPickupPopup;
      this.clearMoveShipmentToBookedPickupData();
    },
    selectBookedPickupToMoveShipmentsIn(val) {
      this.selectedBookedPickup = val;
    },
    getAvailableBookedPickups (pickupDate) {
      const peTransformed = this.parseUnknownDate(pickupDate).toFormat('dd.MM.yyyy');
      this.$store.dispatch('GET_AVAILABLE_BOOKED_PICKUPS', {id: this.$route.params.id, shipmentIds: this.checkedShipmentIds, pickupDate: peTransformed}).then((res) => {
        this.availableBookedPickups = res;
      });
    },
    submitMoveShipmentToBookedPickup() {
      if (!Object.keys(this.selectedBookedPickup).length) {
        console.log('Pickup not selected');
        return;
      }

      const payload = {
        data: {
          ...this.moveShipmentToBookedPickupData
        },
        shipmentIds: this.checkedShipmentIds,
        moveFromPickupId: this.$route.params.id,
        moveToPickupId: this.selectedBookedPickup.id
      };
      this.$store.dispatch('MOVE_SHIPMENTS_TO_BOOKED_PICKUP', payload).then((res) => {
        this.toggleMoveShipmentToBookedPickupPopup();
        this.$store.dispatch('GET_BOOKED_PICKUP_SHIPMENTS', {id: this.$route.params.id});
        if (res.data.data.shipments_count === 0) {
          this.$router.push('/pickups/booked');
        }
      });
    },
    clearMoveShipmentToBookedPickupData() {
      this.moveShipmentToBookedPickupData = {
        pickupDate: ''
      }
      this.availableBookedPickups = [];
      this.selectedBookedPickup = {};
    },
    clearBookPickupData() {
      this.pickupShipments = [];
      this.pickupData = {
        pickupDate: '',
        pickupTimeFrom: '',
        pickupTimeUntil: '',
        reference: '',
        company: '',
        attention: '',
        email: '',
        phone: {
          number: '',
          country: '',
        },
      };
      this.pickupAddress = {
        address_1: '',
        address_2: '',
        post_code: '',
        city: '',
        country_id: '',
        state: '',
      };
    },
    returnHoursArrayFunction(timeZone) {
      this.setBulkPickupTimeRange(this.product, timeZone, false)
    },
    getLocation() {
      return null;
    }
  },
  destroyed() {
    this.$store.dispatch('CLEAR_SINGLE_PICKUP');
  },
  watch: {
    '$route' () {
      this.getComponentData()
    },
    observableObject: {
      handler(newVal, oldVal){
        if(this.popupDataIsValid()){
          this.makeCall();
        }
      },
      deep: true
    },
    'moveShipmentToBookedPickupData.pickupDate': {
      handler(newVal, oldVal){
        if(newVal && (newVal !== oldVal)){
          this.getAvailableBookedPickups(newVal);
        }
      },
      deep: true
    },
    'get_single_pickup.status': function(newStatus) {
      this.status = newStatus;
    },
  },
}
</script>
